import { init as initCompanyType, rules as companyTypeRules } from '../customer-company-type';
import { init as initCustomerNames } from '../customer-names';
import { init as initIdentifier, rules as identifierRules } from './create-account-identifier';
import {
  init as initLoyaltySubscription,
  rules as loyaltySubscriptionRules,
} from './create-account-loyalty-subscription';
import { init as initCustomerNationalId } from '../customer-national-id';
import { init as initTooltip } from './create-account-tooltip';
import { validator, getParameters, getStoredIdentifier, setStoredIdentifier, removeStoredIdentifier, ajaxError, disableSubmit } from '../customer-utils';
import { rules as customerPasswordRules } from '../customer-password';
import { i18n } from '@/commons/utils';

const URL = {
  CREATE: 'rest/model/atg/userprofiling/ProfileActor/create',
};

const CONQUEST = [1, 7];
const SMART = [2, 6];

const rules = {
  rules: {
    notPracticeSport: () => {
      const sportBlock = cube.$id('sport-data');

      return (
        sportBlock &&
        !sportBlock.hasAttribute('data-optional') && {
          or: [
            {
              required: true,
            },
            {
              sportSelected: {
                userSportSelector: '#user-sports--selected',
              },
            },
          ],
        }
      );
    },

    password: {
      extendedalphanumeric: true,
    },

    subscribeOffers: {
      required: true,
    },

    landline: {
      mobile: true,
    },
  },

  messages: {
    notPracticeSport: {
      or: i18n('validationMsg.required'),
    },

    'newletter-agreement': {
      required: i18n('create_account.newsletter.required_terms_and_conditions'),
    },
  },
};

export const init = form => {
  validator(
    form,
    'input[type="text"]:hidden, #password:hidden, #password-confirm:hidden, #js-address-data:hidden select, #js-optin-data:hidden input, #js-create-account-loyalty-subscription:hidden input, #mobile:hidden, #email:hidden, #email-confirmation:hidden, #js-company-data:hidden #company-type, .bs-searchbox input, #js-personal-data-email:hidden input, #js-personal-data-mobile:hidden input, #js-national-id-block:hidden select',
    rules,
    identifierRules,
    companyTypeRules,
    loyaltySubscriptionRules,
    customerPasswordRules
  );

  initIdentifier(form);

  const btobcheckbox = cube.$id('js-btob-checkbox');
  const company = cube.$id('js-company');
  if (btobcheckbox && company) {
    btobcheckbox.on('change', displayComplementaryFields);
    initCompanyType();
  }

  const privacyLinks = cube.$$('.account-form__subtitle__link');
  if (privacyLinks.length) privacyLinks.on('click', scrollToPrivacy);

  const countryCode = cube.$id('countryCodeGeoId');
  if (countryCode) {
    countryCode.on('change', () => cube.form.address.init(countryCode.value, 'BILLING'));
    countryCode.dispatchEvent(new CustomEvent('change'));
  }

  const birthdate = cube.$id('js-birthdate');
  if (birthdate) initDatePicker(birthdate);

  const selectpicker = cube.$$('.selectpicker');
  if (selectpicker.length)
    selectpicker.on('change', event => !event.target.closest('.tel-group') && $(event.target).valid());

  form.on('submit', createAccount);

  if (cube.$id('js-remember-me-tooltip')) initTooltip();
  if (cube.$id('js-national-id')) initCustomerNationalId();
  if (cube.$id('js-middlename-checkbox')) initCustomerNames();
  if (cube.$id('js-create-account-loyalty-subscription')) initLoyaltySubscription();
};

/**
 * Initialize and update the customer birthdate.
 *
 * @param {HTMLInputElement} birthdate The birthdate field.
 */
const initDatePicker = birthdate => {
  const ATGDate = cube.$id('js-atg-birthdate');
  const date = new Date();
  const { minAge, maxAge } = ATGDate.dataset;
  const $birthdate = $(birthdate);
  const value = birthdate.value || null;

  $birthdate.datetimepicker({
    minDate: new Date(date.getFullYear() - maxAge, date.getMonth(), date.getDate()),

    maxDate: new Date(date.getFullYear() - minAge, date.getMonth(), date.getDate()),
  });

  birthdate.value = value;

  $birthdate.on('dp.change focusout', () => {
    if (birthdate.value)
      ATGDate.value = $birthdate
        .data('DateTimePicker')
        .date()
        .format(ATGDate.dataset.dateFormat);
    else ATGDate.value = null;
  });
};

/**
 * Handle complementary fields display.
 *
 */
function displayComplementaryFields() {
  const isB2B = this.checked;

  displayCompanyFields(isB2B);
  displayAddressFields(isB2B);
}

/**
 * Display or hide company fields.
 *
 * @param {Boolean} isB2B
 */
const displayCompanyFields = isB2B => {
  const company = cube.$id('js-company-data');

  if (isB2B) company.classList.remove('hidden');
  else company.classList.add('hidden');
};

/**
 * Display or hide address fields depending of the customer choice B2B or B2C and configuration.
 *
 * @param {Boolean} isB2B
 */
const displayAddressFields = isB2B => {
  const address = cube.$id('js-address-data');
  if (!address) return;

  const showInB2B = address.hasAttribute('data-show-btob-address');
  const showInB2C = address.hasAttribute('data-show-btoc-address');

  if (showInB2C === showInB2B) return;

  const { classList } = address;

  if (showInB2B) {
    if (isB2B) classList.remove('hidden');
    else classList.add('hidden');
  }

  if (showInB2C) {
    if (!isB2B) classList.remove('hidden');
    else classList.add('hidden');
  }
};

/**
 * Update password, loyalty and optin blocks depending on the customer type and password configuration.
 *
 * @param {String} displayPassword The password visibility.
 * @param {Integer} memberType The customer type.
 */
export const updateCreateProcess = (displayPassword, memberType) => {
  const password = cube.$id('js-password-data');
  const input = cube.$id('password');
  const inputLabel = cube.$id('js-password-label');
  const textMandatoryField = i18n('fields.mandatory');
  const textOptionalField = i18n('fields.notMandatory');
  const nopassword = cube.$id('js-no-password');
  const tooltip = cube.$id('js-password-tooltip');
  const tooltipText = cube.$id('js-password-tooltip-text');
  const loyalty = cube.$id('js-loyalty-data');
  const optin = cube.$id('js-optin-data');

  switch (displayPassword) {
    case 'DONTSHOW':
      [password, loyalty, optin, tooltip].forEach(element => element && element.classList.add('hidden'));
      nopassword.classList.remove('hidden');

      if (CONQUEST.includes(memberType)) nopassword.textContent = i18n('createAccount.noPassword.ConquestMessage');
      else if (SMART.includes(memberType)) nopassword.textContent = i18n('createAccount.noPassword.SmartMessage');
      else nopassword.textContent = i18n('createAccount.noPassword.Message');

      break;

    case 'MANDATORY':
      nopassword.classList.add('hidden');
      [password, loyalty, optin, tooltip].forEach(element => element && element.classList.remove('hidden'));

      input.setAttribute('required', '');
      tooltipText.textContent = i18n('createAccount.password.tooltip.mandatoryText');

      if (!inputLabel.textContent.includes(textMandatoryField))
        inputLabel.textContent = inputLabel.textContent.replace(textOptionalField, textMandatoryField);

      break;

    case 'OPTIONAL':
      nopassword.classList.add('hidden');
      [password, loyalty, optin, tooltip].forEach(element => element && element.classList.remove('hidden'));

      input.removeAttribute('required');
      tooltipText.textContent = i18n('createAccount.password.tooltip.optionalText');
      inputLabel.textContent = inputLabel.textContent.replace(textMandatoryField, textOptionalField);

      break;

    default:
  }
};

/**
 * Reload the privacy blocks depending of the cutomer type.
 *
 * @param {Boolean} identifierExists "true" if the customer identifier is already known in the Decathlon database.
 * @param {Integer} memberType The account type of the customer.
 */
export const updatePrivacies = (identifierExists, memberType) => {
  const isCheckout = cube.$id('is-checkout');
  let context;

  if (!isCheckout.value === 'true') context = 'AccountCreation';
  else if (identifierExists && !CONQUEST.includes(memberType)) context = 'CheckoutSmart';
  else context = 'CheckoutRegular';

  cube.cartridge('privacy-rules').reload({ context });
};

/**
 * Scroll to privacy blocks.
 *
 * @param {Event} event
 */
function scrollToPrivacy(event) {
  event.preventDefault();

  const target = cube.$id(this.dataset.target);
  const header = cube.$('.header-checkout') || cube.$id('header');
  const scrollTop = target.getBoundingClientRect().top + window.pageYOffset - header.offsetHeight - 20;

  $('html, body').animate(
    {
      scrollTop,
    },
    500
  );
}

/**
 * Disable or enable all form fields except the identifers fields.
 *
 * @param {HTMLFormElement} form The create account form.
 */
export const disableForm = (form, disabled = true) => {
  if (disabled && form.hasAttribute('data-disabled')) return;

  if (disabled) form.dataset.disabled = '';
  else form.removeAttribute('data-disabled');

  const fieldsets = form.$$('.js-fieldset');

  fieldsets.forEach(fieldset => {
    if (disabled) fieldset.classList.add('account-form__section--disabled');
    else fieldset.classList.remove('account-form__section--disabled');

    const fields = [...form.elements].filter(element => element.closest('.js-fieldset'));

    fields.forEach(field => {
      if (disabled && field.disabled) form.dataset.disabled = '';

      if (!disabled && !field.dataset.disabled) field.disabled = disabled;

      if (field.classList.contains('selectpicker')) $(field).selectpicker('refresh');
    });
  });

  disableSubmit(disabled, 'CREATE');
};

/**
 * Send an XHR to create an account if all fields are ok.
 *
 * @param {Event} event
 */
function createAccount(event) {
  event.preventDefault();

  const button = cube.$id('js-create-account-button');

  if ($(this).valid()) {
    const parameters = getParameters(this);
    const isSmart = !parameters.password;
    const { smsCode } = getStoredIdentifier('MAIN');

    if (cube.$id('js-is-prefilled').value === 'true' && smsCode) {
      if(smsCode !== cube.$id('verification-code')) {
        const newSmsCode = cube.$id('verification-code').value;
        setStoredIdentifier({smsCode: newSmsCode}, 'MAIN');
        parameters.loginVerificationCode = newSmsCode;
      } else {
        parameters.loginVerificationCode = smsCode;
      }


    }

    button.disabled = true;

    cube.ajax
      .post(URL.CREATE, parameters)
      .onSuccess(response => createAccountSuccess(response, this, button, isSmart))
      .onError(response => ajaxError(response, button))
      .call();
  } else {
    cube.alert.error(i18n('create_account.validation.error'));
  }
}

/**
 * Display a message and redirect the customer if the XHR returns no error(s).
 *
 * @param {Object} response The XHR response.
 * @param {HTMLFormElement} form The create account form.
 * @param {HTMLButtonElement} button The submit button.
 * @param {Boolean} isSmart "true" if there is no password for the created account.
 */
const createAccountSuccess = (response, form, button, isSmart) => {
  if (ajaxError(response, button)) return;

  const { redirect, url } = response.responseTO.data;

  cube.alert.success(i18n(`${isSmart ? 'create_account.smart.success' : 'createAccount.success'}`), 'chrono');

  removeStoredIdentifier();
  if (window.cube.config.anly_useLegacy === 'Y') {
    CUBE.Analytics.queue.send('accountCreated');
  }

  setTimeout(() => {
    if (redirect) window.location.replace(url);
    else window.location.href = form.dataset.redirect || '/';
  }, 3000);
};



// WEBPACK FOOTER //
// ./src/customer/create-account/create-account.js